import { ChangeDetectionStrategy, Component, computed, effect, inject, input, Signal, signal, WritableSignal } from '@angular/core';
import { ContentDatasetDto, ContentSelectorDashboardDto, ContentSelectorItemDto, ContentSelectorVisualDto, DataDateFormat, DataTimeFormat } from 'src/app/services/api.services';
import { ContentSelectorDialog } from './dialog';
import { DataModelResolver } from '../../dataModel/DataModelResolver';
import { DataFormatter } from '../../formatting/DataFormatter';
import { DataType } from '../../dataModel/DataType';
import { ItemMetadata } from './ContentSelectorPreview.component';
import { getInsightsPbiChartType } from 'src/app/shared/chart/chart';
import { DataModel } from '../../dataModel';
import { VisualFieldTargetResolver } from '../../visuals/VisualFieldTargetResolver';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { FilterUtils } from '../../utils/FilterUtils';
import { getTitleForKnownProduct } from 'src/app/shared/utils';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, share, shareReplay, Subscription, switchMap, takeWhile, tap, timer } from 'rxjs';
import { AppCurrencies } from 'src/app/state/app/app.reducer';
import { AppCurrencyService } from 'src/app/app/AppCurrencyService';

@Component({
  selector: 'content-selector__dashboard-preview',
  template: `
    <content-selector-preview [loaded]="true" [metadata]="metadata()">
      @if (showEmbed()) {
        <dashboard-embed [dashboardId]="dashboardId()"></dashboard-embed>
      }
    </content-selector-preview>
    `,
  styles: `
    :host {
      display: contents;
    }
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContentSelectorDashboardPreviewComponent {
  readonly item = input<ContentSelectorDialog.Item<ContentSelectorDashboardDto>>();
  readonly visible = input(false);
 
  readonly metadata = computed(() => {
    let item = this.item();
    if (!item)
      return [];

    return getDashboardMetadata(item.data, DataFormatter.createDefault({
      currencies: AppCurrencies.fallback
    }));
  });

  readonly dashboardId = computed(() => this.item()?.data?.dashboard?.id);
  
  readonly showEmbed = signal(false);

  constructor() {
    hideTimeout(this.visible, this.showEmbed);
  }
}

@Component({
  selector: 'content-selector__visualPreview',
  template: `
    <content-selector-preview [loaded]="loaded()" [metadata]="metadata()">
      @if (showEmbed()) {
        <visual-embed [visualId]="visualId()"></visual-embed>
      }
    </content-selector-preview>
    `,
  styles: `
    :host {
      display: contents;
    }
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContentSelectorVisualPreviewComponent {
  readonly dataModelResolver = inject(DataModelResolver);
  readonly currencyService = inject(AppCurrencyService);
  readonly item = input<ContentSelectorDialog.Item<ContentSelectorVisualDto>>();
  readonly visible = input(false);

  readonly dataModel$ = toObservable(this.item).pipe(
    map(x => x?.data?.visual?.schema),
    filter(x => !!x),
    switchMap(x => this.dataModelResolver.getModel(x)),
    shareReplay(1)
  );

  readonly dataModel = toSignal(this.dataModel$);

  readonly metadata = computed(() => {
    let item = this.item();
    if (!item)
      return [];

    let dataModel = this.dataModel();
    if (!dataModel)
      return [];

    return getVisualMetadata(
      item.data,
      DataFormatter.createDefault({
        currencies: this.currencyService.currencies()
      }),
      this.currencyService.currencies,
      dataModel);
  });

  readonly visualId = computed(() => this.item()?.data?.visual?.id);

  readonly loaded = computed(() => !!this.dataModel());

  readonly showEmbed = signal(false);

  constructor() {
    hideTimeout(this.visible, this.showEmbed);
  }
}

function hideTimeout(visible: Signal<boolean>, showEmbed: WritableSignal<boolean>) {
  let hideTimeout = timer(5000).pipe(
    takeUntilDestroyed(),
    tap(() => showEmbed.set(false))
  );

  let hideTimeoutSub: Subscription;

  let cleanup = () => {
    hideTimeoutSub?.unsubscribe();
    hideTimeoutSub = null;
  };

  // Show embed when visible, but only hide it after delay
  // This way if user goes back, they can see previously loaded content
  // Don't wanna load too many though, that's why it will eventually be hidden
  effect(onCleanup => {
    if (visible()) {
      cleanup();
      showEmbed.set(true);
    }
    else {
      hideTimeoutSub = hideTimeout.subscribe();
      onCleanup(cleanup);
    }
  });
}


function getCommonMetadata(
  item: ContentSelectorItemDto,
  dataFormatter: DataFormatter) {
  let metadata = [
    new ItemMetadata('Description', [{ title: item.description }])
  ];
      
  if (item.fromLibrary) {
    metadata.push(
      new ItemMetadata('Categories', item.categories.map(x => ({ title: x.name })))
    );
  }
  else {
    let lastModifiedAt = dataFormatter.formatForDisplay(
      item.lastModifiedAt,
      {
        dataType: DataType.DateTime,
        display: {
          dateFormat: DataDateFormat.Short,
          timeFormat: DataTimeFormat.NoSeconds
        }
      });

    metadata.push(
      new ItemMetadata('Shared', [{ title: item.shared ? 'Yes' : 'No' }]),
      new ItemMetadata('Modified', [{ title: `${item.lastModifiedBy} - ${lastModifiedAt}` }]),
      new ItemMetadata('Owner', [{ title: item.ownerName }])
    );
  }

  return metadata;
}
  
function getDashboardMetadata(
  d: ContentSelectorDashboardDto,
  dataFormatter: DataFormatter): ReadonlyArray<ItemMetadata> {
  let metadata = [
    new ItemMetadata('Visuals', d.dashboard?.visuals?.map(v => ({ title: v.name })) ?? []),
    new ItemMetadata('Dataset(s)', d.datasets?.map(x => ({ title: formatDatasetTitle(x) })) ?? []),
    ...getCommonMetadata(d, dataFormatter)
  ];

  return metadata;
}
    
function getVisualMetadata(
  v: ContentSelectorVisualDto, 
  dataFormatter: DataFormatter,
  currencies: AppCurrencies | Signal<AppCurrencies>,
  model: DataModel): ReadonlyArray<ItemMetadata> {
  let chartType = getInsightsPbiChartType(v.visual?.type);
      
  let fieldResolver = VisualFieldTargetResolver.create({
    currencies,
    dataModel: model,
    customFields: v.visual.customFields,
    schema: v.visual.schema
  });

  let fields = v.visual?.fields
    ?.map(f => ({ f: f.target, i: fieldResolver.find(f.target) }))
    .map(({ f, i }) => {
      if (!i) {
        return {
          title: SchemaUtils.getTargetDisplayString(f),
          tooltip: SchemaUtils.getFieldTargetString(f),
          invalid: true,
        };
      }
      else {
        return {
          title: i.measureTitle,
          tooltip: i.measureTitle,
          invalid: i.isValid,
        };
      }
    }).filter(x => !!x) ?? [];

  let filterFormatter = new FilterUtils.Formatter(fieldResolver, dataFormatter);

  let filters = v.visual?.filters?.map(f => {
    let target = FilterUtils.getTarget(f);
    if (!target) 
      return null;

    let item = fieldResolver.find(target);

    return {
      title: filterFormatter.formatDescription(f, { capitalizeFirstWord: true }),
      tooltip: filterFormatter.formatDescription(f, { capitalizeFirstWord: true, longFieldTitles: true, noHtml: true }),
      invalid: !item?.isValid,
    };
  }).filter(x => !!x) ?? [];

  let metadata = [];

  if (v.visual?.subtitle) {
    metadata.push(new ItemMetadata('Subtitle', [({ title: v.visual.subtitle })]));
  }

  metadata.push(...[
    new ItemMetadata('Type', [({ title: chartType?.title })]),
    new ItemMetadata('Dataset', [({ title: formatDatasetTitle(v.dataset) })]),
    new ItemMetadata('Fields', fields),
    new ItemMetadata('Filters', filters),
    ...getCommonMetadata(v, dataFormatter)
  ]);
      
  return metadata;
}

function formatDatasetTitle(d: ContentDatasetDto) {
  if (!d)
    return null;

  if (SchemaUtils.isCore(d.schemaTarget)) {
    let productPrefix1 = `${d.schemaTarget.product} - `;
    let productPrefix2 = `${getTitleForKnownProduct(d.schemaTarget.product) ?? d.schemaTarget.product} - `;
    if (d.name.toLowerCase().startsWith(productPrefix1.toLowerCase()))
      return `${productPrefix2}${d.name.substring(productPrefix1.length)}`;

    if (!d.name.toLowerCase().startsWith(productPrefix2.toLowerCase()))
      return `${productPrefix2}${d.name}`;
  }
      
  return d.name;
}
